/**
 * 划词翻译 — 监听选中文本，弹出翻译浮层
 *
 * 监听 mouseup/touchend，获取 window.getSelection()，
 * 调用 AI translate（短文本）或显示「需配置 AI」提示。
 *
 * 对齐 features.md 4.2：划词翻译
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Languages, Loader2, Bookmark, StickyNote, MessageCircle } from 'lucide-react';
import { isConfigured } from '../../lib/ai/config';
import { translateWordOrPhrase } from '../../lib/dictionary';
import { knowledgeRepository } from '../../lib/repository/knowledge';
import { useReaderStore } from '../../stores/reader';
import { useAnnotationStore } from '../../stores/annotation';

interface FloatPos {
  x: number;
  y: number;
}

export function SelectionTranslate({ docId, blockId }: { docId: string; blockId?: string }) {
  const [selectedText, setSelectedText] = useState('');
  const [pos, setPos] = useState<FloatPos | null>(null);
  const [translation, setTranslation] = useState('');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const configured = isConfigured();

  const handleSelection = useCallback(async () => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) {
      setPos(null);
      return;
    }

    const text = selection.toString().trim();
    if (!text || text.length < 2 || text.length > 500) {
      setPos(null);
      return;
    }

    // 获取选区位置
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      setPos(null);
      return;
    }

    setSelectedText(text);
    setTranslation('');
    setSaved(false);
    setPos({ x: rect.left + rect.width / 2, y: rect.bottom + window.scrollY + 8 });

    // 翻译：单词优先离线词典，短语/句子走 AI
    setLoading(true);
    try {
      const { source, result } = await translateWordOrPhrase(text, { aiConfigured: configured });
      setTranslation(source === 'offline' ? `📖 离线词典：${result}` : result);
    } catch {
      setTranslation('翻译失败，请重试');
    } finally {
      setLoading(false);
    }
  }, [configured]);

  useEffect(() => {
    document.addEventListener('mouseup', handleSelection);
    document.addEventListener('touchend', handleSelection);
    return () => {
      document.removeEventListener('mouseup', handleSelection);
      document.removeEventListener('touchend', handleSelection);
    };
  }, [handleSelection]);

  // 点击外部关闭
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setPos(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function handleSaveWord() {
    if (!selectedText) return;

    // 从选区附近的 DOM 推断 blockId
    let inferredBlockId = blockId;
    if (!inferredBlockId) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        let node: Node | null = selection.getRangeAt(0).startContainer;
        while (node && node !== document.body) {
          if (node instanceof Element && node.hasAttribute('data-block-id')) {
            inferredBlockId = node.getAttribute('data-block-id') ?? undefined;
            break;
          }
          node = node.parentElement;
        }
      }
    }

    try {
      const docTitle = useReaderStore.getState().docIndex?.meta.title;
      await knowledgeRepository.addWord(
        selectedText,
        docId,
        inferredBlockId,
        docTitle,
        selectedText
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error('[Hump] Save word from selection failed:', e);
    }
  }

  // 8.2 文本划线批注：打开 AnnotationEditor，把选中文本作为初始内容
  function handleAnnotate() {
    if (!selectedText) return;

    // 从选区附近的 DOM 推断 blockId（复用收藏生词的推断逻辑）
    let inferredBlockId = blockId;
    if (!inferredBlockId) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        let node: Node | null = selection.getRangeAt(0).startContainer;
        while (node && node !== document.body) {
          if (node instanceof Element && node.hasAttribute('data-block-id')) {
            inferredBlockId = node.getAttribute('data-block-id') ?? undefined;
            break;
          }
          node = node.parentElement;
        }
      }
    }
    if (!inferredBlockId) return;

    // 关闭划词浮层，打开批注编辑器（选中文本作为初始内容）
    setPos(null);
    useAnnotationStore.getState().openEditor(docId, inferredBlockId, selectedText);
  }

  if (!pos || !selectedText) return null;

  return (
    <div
      ref={containerRef}
      className="fixed z-50 max-w-sm bg-white dark:bg-warm-dark rounded-lg shadow-lg border border-border-warm dark:border-border-warm-dark p-3"
      style={{
        left: Math.min(pos.x - 160, window.innerWidth - 340),
        top: pos.y - window.scrollY + window.innerHeight > window.innerHeight - 120
          ? pos.y - window.scrollY - 100
          : pos.y - window.scrollY + 8,
      }}
    >
      {/* 选中文本 */}
      <p className="text-sm font-medium text-charcoal dark:text-charcoal-light mb-2 line-clamp-2">
        {selectedText}
      </p>

      {/* 翻译结果 */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-charcoal/50 dark:text-charcoal-light/50">
          <Loader2 className="w-3 h-3 animate-spin" />
          翻译中...
        </div>
      ) : translation ? (
        <p className="text-sm text-charcoal/70 dark:text-charcoal-light/70">{translation}</p>
      ) : !configured ? (
        <p className="text-xs text-charcoal/40 dark:text-charcoal-light/40">
          请先在设置中配置 AI API Key
        </p>
      ) : null}

      {/* 操作按钮 */}
      <div className="flex items-center gap-3 mt-2 pt-2 border-t border-border-warm dark:border-border-warm-dark">
        <button
          onClick={handleSaveWord}
          disabled={saved}
          className="flex items-center gap-1 text-xs text-camel hover:text-camel-dark disabled:opacity-50"
        >
          <Bookmark className="w-3 h-3" />
          {saved ? '已收藏' : '收藏生词'}
        </button>
        {/* 8.2 文本划线批注：打开 AnnotationEditor，选中文本作为初始内容 */}
        <button
          onClick={handleAnnotate}
          className="flex items-center gap-1 text-xs text-camel hover:text-camel-dark"
        >
          <StickyNote className="w-3 h-3" />
          批注
        </button>
        {/* 5.3 划词追问 AI */}
        <button
          onClick={() => {
            // 打开 GuideChat 并注入选中文本作为问题
            const readerStore = (window as any).__humpReaderStore;
            if (readerStore) {
              readerStore.getState().setGuideOpen(true);
              // 延迟一下等 GuideChat 打开
              setTimeout(() => {
                const chatStore = (window as any).__humpChatStore;
                if (chatStore && selectedText) {
                  chatStore.getState().sendMessage(docId, `请解释这段内容：${selectedText}`);
                }
              }, 200);
            }
          }}
          disabled={!configured}
          className="flex items-center gap-1 text-xs text-camel hover:text-camel-dark disabled:opacity-50"
        >
          <MessageCircle className="w-3 h-3" />
          问 AI
        </button>
        {!configured && (
          <span className="flex items-center gap-1 text-xs text-charcoal/40 dark:text-charcoal-light/40">
            <Languages className="w-3 h-3" />
            需 AI Key
          </span>
        )}
      </div>
    </div>
  );
}
